import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { environment } from '../../../environments/environment';
import { Portfolio } from '../../../interfaces/idefend/portfolio.interface';
import { PortfolioGroups } from '../../../interfaces/idefend/portfolio-group.interface';
import { Currency } from '../../../interfaces/idefend/currency.interface';
import { LocalizationSetting } from '../../../interfaces/idefend/localization-settings.interface';
import { SodanReplayHeads } from '../../../interfaces/idefend/sodan-replay-heads.interface';
import { PolicyGroupPayload } from '../../../interfaces/idefend/policy-groups-payload.interface';
import { PolicyGroupResponse } from '../../../interfaces/idefend/policy-groups-response.interface';
import { QuestionManagementReplyPayload } from '../../../interfaces/idefend/question-reply-payload.interface';
import { ReplyResponse } from '../../../interfaces/idefend/question-reply-response.interface';
import { PaginationParams } from '../../../interfaces/idefend/pagination-param.interface';
import { VehiclesCategory } from '../../../interfaces/idefend/vehilces-category.interface';
import { VehicleUsage } from '../../../interfaces/idefend/vehicle-usage.interface';
import { Observable } from 'rxjs';
import { VehicleMake } from '../../../interfaces/idefend/vehicle-make.interface';
import { VehicleModel } from '../../../interfaces/idefend/vehicle-model.interface';
import {
  AvailableClaimLimitsRequest,
  AvailableClaimLimitsResponse,
} from '../shared/interfaces/available-claim-limits.interface';
import { CalculateOfferPayload } from '../shared/interfaces/calculate-offer-payload.interface';
import { CalculateOfferResponse } from '../shared/interfaces/calculate-offer-response.interface';
import { CreationLockPayload } from '../shared/interfaces/creation-lock-payload.interface';
import { CreationLockResponse } from '../shared/interfaces/creation-lock-response.interface';
import { ConfirmSignaturePayload } from '../../../interfaces/idefend/confirm-signature-payload.interface';
import { take } from 'rxjs/operators';
import { mapOfferLockPayload } from './helpers/offer-lock-payload-mapper';
import { GetPolicy, LockOffer, SignOffer } from './store/offer.action';
import { Store } from '@ngxs/store';
import { OfferState } from './store/offer.state';
import { PolicyCreatorState } from './store/policy-creator.state';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { captureSentryException } from '../../utils/sentry-http-error.util';

export interface UploadType {
  id: number;
  code: string;
  name: string;
  description: string;
}

export interface UploadedDocument {
  originalFileName: string;
  uploadType: UploadType;
  uploadedOn: string;
  visibility: 'public' | 'private';
  approved: boolean;
  rejectionReason?: string;
}

@Injectable({
  providedIn: 'root',
})
export class PolicyCreatorService {
  private apiBase = environment.apiBase;
  public characterPattern = /\p{L}/u;
  public digitOrSymbolPattern = /\P{L}/gu;
  public isDialogOpen = false;
  public ALLOWED_PORTFOLIO_PRODUCTS = [
    {
      productCode: '5_DCGAP_M25_GEN',
      productDerivativeAlias: 'MAX',
      order: 1,
    },
    {
      productCode: '5_DCGAP_MG25_GEN',
      productDerivativeAlias: 'MAX AC',
      order: 2,
    },
    {
      productCode: '5_DTGAP_M25_GEN',
      productDerivativeAlias: 'T-MAX',
      order: 3,
    },
    {
      productCode: '4_DTGAP_MG25_GEN',
      productDerivativeAlias: 'T-MAX AC',
      order: 4,
    },
    {
      productCode: '5_DCGAP_F25_GEN',
      productDerivativeAlias: 'FLEX',
      order: 5,
    },
    {
      productCode: '5_DCGAP_FG25_GEN',
      productDerivativeAlias: 'FLEX GO',
      order: 6,
    },
  ];

  intervalId;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private http: HttpClient,
    private store: Store,
    private router: Router,
  ) {}

  handleCityValidation(event: KeyboardEvent) {
    const pattern =
      /^([a-zA-Z\u0080-\u024F]+(?:. |-| |'))*[a-zA-Z\u0080-\u024F]*$/gm;
    // @ts-ignore-next-line
    let value = event.target.value;

    while (!pattern.test(value)) {
      value = value.slice(0, -1);
    }

    // @ts-ignore-next-line
    event.target.value = value;
  }

  filterNonAlphaChars(event) {
    if (event.key.length === 1) {
      if (!this.characterPattern.test(event.key)) {
        event.target.value = event.target.value.slice(
          0,
          event.target.value.length - 1,
        );
      }
    }

    if (this.digitOrSymbolPattern.test(event.target.value)) {
      event.target.value = event.target.value.slice(
        0,
        event.target.value.search(this.digitOrSymbolPattern),
      );
    }
  }

  clearInterval(): void {
    clearInterval(this.intervalId);
  }

  fetchJwtToken(): Observable<void> {
    return this.http.get<void>(`${this.apiBase}/idefend/jwt-token`);
  }

  /* Initial APK steps */
  getPortfolios(): Observable<Portfolio[]> {
    return this.http.get<Portfolio[]>(`${this.apiBase}/idefend/portfolios`);
  }

  getGroups(): Observable<PortfolioGroups[]> {
    return this.http.get<PortfolioGroups[]>(
      `${this.apiBase}/idefend/product-portfolio/groups`,
    );
  }

  getCurrency(id: number): Observable<Currency> {
    return this.http.get<Currency>(`${this.apiBase}/idefend/currencies/${id}`);
  }

  getLocalizationSettings(id: number): Observable<LocalizationSetting> {
    return this.http.get<LocalizationSetting>(
      `${this.apiBase}/idefend/localization-settings/${id}`,
    );
  }

  setSodanReplyHeads(): Observable<SodanReplayHeads> {
    return this.http.post<SodanReplayHeads>(
      `${this.apiBase}/idefend/sodan-reply-heads`,
      {},
    );
  }

  scrollToTop() {
    setTimeout(() => {
      if (window.scrollY !== 0) {
        window.scrollTo(0, 0);
      }
    }, 100);
  }

  setPolicyGroups(
    payload: PolicyGroupPayload,
  ): Observable<PolicyGroupResponse> {
    return this.http.post<PolicyGroupResponse>(
      `${this.apiBase}/idefend/policy-groups`,
      payload,
    );
  }

  patchQuestionReply(
    payload: QuestionManagementReplyPayload,
    query: { locale: string },
    id: number,
  ): Observable<ReplyResponse> {
    return this.http.put<ReplyResponse>(
      `${this.apiBase}/idefend/question-management/replies/${id}`,
      payload,
      {
        params: query,
      },
    );
  }
  /* Initial APK steps */

  getVehicleCategories(
    query: PaginationParams,
  ): Observable<VehiclesCategory[]> {
    const queryDto = new HttpParams({
      // @ts-ignore-next-line
      fromObject: query,
    });

    return this.http.get<VehiclesCategory[]>(
      `${this.apiBase}/idefend/vehicles/categories`,
      {
        params: queryDto,
      },
    );
  }

  getVehicleUsages(query: PaginationParams): Observable<VehicleUsage[]> {
    const queryDto = new HttpParams({
      // @ts-ignore-next-line
      fromObject: query,
    });

    return this.http.get<VehicleUsage[]>(
      `${this.apiBase}/idefend/vehicles/usages`,
      {
        params: queryDto,
      },
    );
  }

  getVehicleMakes(code?: string): Observable<VehicleMake[]> {
    const queryDto = new HttpParams({
      // @ts-ignore-next-line
      fromObject: {
        categoryCode: code ?? '',
      },
    });

    return this.http.get<VehicleMake[]>(
      `${this.apiBase}/idefend/vehicles/makes`,
      {
        params: queryDto,
      },
    );
  }

  getVehicleModels(): Observable<VehicleModel[]> {
    return this.http.get<VehicleModel[]>(
      `${this.apiBase}/idefend/vehicles/models`,
    );
  }

  getOffer(payload: CalculateOfferPayload): Observable<CalculateOfferResponse> {
    return this.http.post<CalculateOfferResponse>(
      `${this.apiBase}/idefend/policies/creation/calculate-offer`,
      payload,
    );
  }

  getAvailableClaimLimits(
    request: AvailableClaimLimitsRequest,
  ): Observable<AvailableClaimLimitsResponse> {
    return this.http.post<AvailableClaimLimitsResponse>(
      `${this.apiBase}/idefend/policies/creation/available-claim-limits`,
      request,
    );
  }

  lockOffer(payload: CreationLockPayload): Observable<CreationLockResponse> {
    return this.http.post<CreationLockResponse>(
      `${this.apiBase}/idefend/policies/creation/lock`,
      payload,
    );
  }

  /* TODO: check response */
  signOffer(id: number, payload: ConfirmSignaturePayload): Observable<unknown> {
    return this.http.put(
      `${this.apiBase}/idefend/policies/${id}/confirm-signature`,
      payload,
    );
  }

  getPolicyByNumber(policyNo: string) {
    return this.http.get<CreationLockResponse[]>(
      `${this.apiBase}/idefend/policies?policyNo=${policyNo}`,
    );
  }

  getPolicy(id: number): Observable<CreationLockResponse> {
    return this.http.get<CreationLockResponse>(
      `${this.apiBase}/idefend/policies/${id}`,
    );
  }

  getAttachments(policyId: number, documentType: string): Observable<any> {
    return this.http.get(
      `${this.apiBase}/idefend/policies/${policyId}/document-download/${documentType}`,
    );
  }

  checkPopupBlocker() {
    if (isPlatformBrowser(this.platformId)) {
      try {
        const testPopup = window.open('', '_blank', 'width=1,height=1');
        if (testPopup === null || typeof testPopup === 'undefined') {
          alert(
            'Płatność otworzy się w nowym oknie. Włącz w przeglądarce obsługę wyskakujących okienek dla tej strony.',
          );
          return true;
        } else {
          testPopup.close();
          return false;
        }
      } catch (e) {
        Sentry.captureException(e);
      }
    }
  }

  handlePolicySigning(): void {
    const payload = mapOfferLockPayload(
      this.store.selectSnapshot(PolicyCreatorState.state),
      this.store.selectSnapshot(OfferState.offer),
    );

    if (!payload) {
      Sentry.captureMessage('Failed to create offer lock payload');
      return;
    }

    const paymentMethod = this.store.selectSnapshot(
      PolicyCreatorState.paymentMethod,
    );

    this.store
      .dispatch(new LockOffer(payload))
      .pipe(take(1))
      .subscribe({
        next: () => {
          const offerLocked = this.store.selectSnapshot(
            OfferState.offerLocked,
          );

          if (!offerLocked) {
            Sentry.captureMessage('LockOffer completed without locked offer');
            return;
          }

          if (paymentMethod === 'PM_BT') {
            this.router.navigateByUrl(`/cta/upload/${offerLocked.number}`);
            return;
          }

          if (payload.signatureTypeCode === 'AUTHORIZED_BY_PAYMENT') {
            this.openPaymentAndPoll(offerLocked);
            return;
          }

          this.store
            .dispatch(
              new SignOffer(offerLocked.id, {
                confirmationCode: offerLocked.confirmationCode,
                confirmedOn: new Date().toISOString(),
              }),
            )
            .pipe(take(1))
            .subscribe({
              next: () => this.openPaymentAndPoll(offerLocked),
              error: (err) => captureSentryException(err),
            });
        },
        error: (err) => captureSentryException(err),
      });
  }

  private openPaymentAndPoll(offerLocked: CreationLockResponse): void {
    if (!this.checkPopupBlocker() && offerLocked.paymentInitiationUrl) {
      window.open(
        this.sanitizeUrl(offerLocked.paymentInitiationUrl),
        '_blank',
        'noopener,noreferrer',
      );
    }

    this.startPaymentPolling(offerLocked.id);
  }

  private startPaymentPolling(policyId: number): void {
    this.clearInterval();
    this.intervalId = setInterval(() => {
      this.store.dispatch(new GetPolicy(policyId));
    }, 5000);
  }

  getUploadedPolicyDocuments(policyId: number): Observable<UploadedDocument[]> {
    return this.http.get<UploadedDocument[]>(
      `${this.apiBase}/idefend/policies/${policyId}/uploads`,
    );
  }

  uploadPolicyDocuments(policyId: number, payload: FormData): Observable<void> {
    return this.http.post<void>(
      `${this.apiBase}/idefend/policies/${policyId}/uploads`,
      payload,
    );
  }

  getDiscountCode(discountCode: string): Observable<any> {
    return this.http.get(`${this.apiBase}/discount/code/${discountCode}`);
  }

  getDiscountFromLink(discountLink: string): Observable<any> {
    return this.http.get(`${this.apiBase}/check-link/${discountLink}`);
  }

  private sanitizeUrl(url: string): string {
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }

    if (url.includes('://')) {
      Sentry.captureMessage('Removed unsafe protocol from payment URL');
      return 'https://' + url.split('://')[1];
    }

    return 'https://' + url;
  }
}
